import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { toLowerCase } from 'bl/validation/toLowerCase.bl';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import validatePassword from 'bl/validation/validatePassword.bl';
import validatePhone from 'bl/validation/validatePhone.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import FormError from 'components/form/FormError.component';
import FormField from 'components/form/FormField.component';
import PasswordInput from 'components/form/PasswordInput.component';
import TextInput from 'components/form/TextInput.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import CONF from 'config/conf.singleton';
import SEGMENT from 'config/segment.singleton';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import selectCognitoUserNameExhaustively from 'selectors/selectCognitoUserNameExhaustively.selector';
import {
  COGNITO_STATE,
  rcmClearCacheAction,
  selectCognitoConfig,
  selectCognitoErrorMessage,
  selectCognitoState,
  selectCognitoUserPool,
  xeRegisterUser,
} from 'utils/cognito.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromPropsToQueryString } from 'utils/converters/props/path.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import withFeedback from 'utils/functions/withFeedback.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parsePhone from 'utils/parsers/parsePhone.util';
import batchValidate from 'utils/predicates/batchValidate.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';

import Header from 'onboarding/common/Header';
import OnboardingFooter from 'onboarding/common/OnboardingFooter';
import { trackEvent } from 'tracking/actions';

import CN from './RegisterPage.module.scss';

const CTAG = 'RegisterPage';
const L = logger(CTAG);

const MARKETING_SITE_TAS = `${CONF.marketingSite}/terms-and-conditions`;

const DEFAULT_COUNTRY_CODE = 'GB';
const NAME_TEMPLATE = 'New Best Friend';

const F = Object.freeze({
  name: 'name',
  mail: 'email',
  pass: 'password',
  phon: 'phone',
});

const MESSAGE = Object.freeze({
  [F.name]: 'Please enter a name',
  [F.mail]: 'Please enter a valid email',
  [F.pass]: 'Password needs to be at least 8 characters in length and contain an uppercase character and a number.',
  [F.phon]: 'Please enter valid phone number',
});

const VALIDATOR = Object.freeze({
  [F.name]: validateName,
  [F.mail]: validateEmail,
  [F.pass]: validatePassword,
  [F.phon]: ({ value }) => validatePhone({ value, uk: true }),
});

const RegisterPage = (props) => {
  const search = fromPropsToQueryString(props);
  const nav = navFor(CTAG, { history: props.history });

  const { cognitoErrorMessage, xeRegisterUser, trackEvent, userPool, config, cognitoUsername, cognitoState } = props;

  const isRegistered = cognitoState === COGNITO_STATE.in || cognitoState === COGNITO_STATE.needConfirmation;
  const isChecking = cognitoState === COGNITO_STATE.loading;

  const [fields, setFields] = useStateWhileMounted({ [F.name]: S.empty, [F.phon]: S.empty, [F.mail]: S.empty });
  const [cognitoError, setCognitoError] = useState(cognitoErrorMessage);
  const { val: isLoading, set: setIsLoading } = useTip(false);

  const isNextEnabled = batchValidate({ validators: VALIDATOR, fields });

  const name = fields[F.name];
  const email = fields[F.mail];
  const password = fields[F.pass];
  const phoneNumber = fields[F.phon];

  const handleDismiss = useCallback(() => void setCognitoError(''), []);
  const handleLogin = useCallback(() => void nav.push({ pathname: LINK.login }), [nav]);

  const handleChange = useCallback(
    (ev) => {
      const { name: fieldName, raw } = asObject(ev);
      const value = fieldName === F.mail ? toLowerCase(raw) : raw;
      if (fields[fieldName] === value) return;

      setFields({ ...fields, [fieldName]: value });
    },
    [fields, setFields],
  );

  const handleValidate = useCallback((ev) => {
    const { name: fieldName, dirty, raw: value } = asObject(ev);
    const validate = VALIDATOR[fieldName];

    // no point validating if there wasn't even a single change
    if (!dirty) return null;
    if (isNotFn(validate)) return null;

    // return null to clear the error from before
    return { error: validate({ value }) ? null : MESSAGE[fieldName] };
  }, []);

  const [, handleClick] = useAfn(async () => {
    if (!isNextEnabled) return;

    try {
      const pn = parsePhone({ phoneNumber, countryCode: DEFAULT_COUNTRY_CODE });
      const attributes = pn ? { name, email, phone_number: pn } : { name, email };
      const options = { userPool, config, username: email, password, attributes };

      const register = withFeedback((value) => void setIsLoading(value || isChecking), xeRegisterUser);
      const result = await register(options);
      L.debug('handleClick()', 'result:', result);

      trackEvent('NEW_USER_CREATED');
      nav.push({ pathname: LINK.onboardingType, search });
    } catch (e) {
      L.warn('handleClick()', 'phoneNumber:', phoneNumber, e);
      setCognitoError(e);
    }
  }, [
    isNextEnabled,
    xeRegisterUser,
    phoneNumber,
    name,
    email,
    userPool,
    config,
    password,
    setIsLoading,
    isChecking,
    trackEvent,
    nav,
    search,
  ]);

  useEffect(() => {
    if (!isRegistered) return;

    // a registered user doesn't need this form, so go to the next one

    SEGMENT.identify({ id: cognitoUsername });
    L.info('useEffect()', { cognitoState, cognitoUsername }, 'moving on to the next step...');
    nav.push({ pathname: LINK.onboardingType, search });
  }, [cognitoErrorMessage, cognitoUsername, cognitoState, nav, search, isRegistered]);

  L.debug('()', fields);

  if (isRegistered) {
    // if user is logged in, the creation step is finished, just return null
    L.debug('()', 'User already isRegistered:', isRegistered, 'returning', null);
    return null;
  }

  return (
    <div data-bem="RegisterPage" className={CN.component}>
      <SalonModal tag="RegisterPage__cognitoError" isOpen={fromAnyToBool(cognitoError)}>
        <SalonModal.Body>
          <IconStatus variant="alert" width="md">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>Error</div>
            <div className={CN.modalText}>
              <FormError variant="dialog" value={cognitoError} />
            </div>
          </div>
        </SalonModal.Body>

        <SalonModal.Footer>
          <div className={CN.buttons}>
            <Button variant="outlineForce" casing="dialog" onClick={handleLogin}>
              Login
            </Button>
            <Button variant="primaryForce" casing="dialog" onClick={handleDismiss}>
              Okay
            </Button>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <Header
        titleFirstPart="Hey, "
        userName={fields[F.name] || NAME_TEMPLATE}
        titleSecondPart="!"
        description="Let's learn about you."
      />

      <div data-bem="RegisterPageV2__Fields" className={CN.content}>
        <FormField
          input={TextInput}
          name={F.name}
          label="Your Full Name"
          placeholder="Lola Jackson"
          value={name}
          autoFocus
          onChange={handleChange}
          onValidate={handleValidate}
        />

        <FormField
          input={TextInput}
          name={F.mail}
          label="Your Email Address"
          placeholder="lola@missjackson.com"
          value={email}
          onChange={handleChange}
          onValidate={handleValidate}
        />

        <FormField
          input={TextInput}
          inputMode="decimal"
          name={F.phon}
          label="Your Mobile Number"
          placeholder="07775 332200"
          value={phoneNumber}
          onChange={handleChange}
          onValidate={handleValidate}
        />

        <FormField
          input={PasswordInput}
          type="password"
          autoComplete="new-password"
          name={F.pass}
          label="Create a Password"
          placeholder="●●●●●●●●"
          value={password}
          onChange={handleChange}
          onValidate={handleValidate}
        />
      </div>

      <OnboardingFooter isFooterButtonActive={isNextEnabled} onButtonClick={handleClick} isLoading={isLoading} />

      {isLoading && <div data-bem="RegisterPageV2__loading" className={CN.loadBlocker} />}

      <div data-bem="RegisterPageV2__footer" className={CN.additional}>
        <span data-bem="RegisterPageV2__disclaimer" className={CN.disclaimer}>
          By providing your details above you agree to the Slick
        </span>
        <a href={MARKETING_SITE_TAS} className={CN.toc}>
          Terms and Conditions
        </a>
      </div>
    </div>
  );
};

const RegisterPageV2Hoc = connect(
  (state) => ({
    cognitoUsername: selectCognitoUserNameExhaustively(state),
    cognitoErrorMessage: selectCognitoErrorMessage(state),
    userPool: selectCognitoUserPool(state),
    config: selectCognitoConfig(state),
    cognitoState: selectCognitoState(state),
  }),
  mapActionsToDispatchToProps({
    trackEvent,
    xeRegisterUser,
    clearCache: rcmClearCacheAction,
  }),
)(RegisterPage);

// this extra export is needed for tests that provide their own mock for connect()
export { RegisterPage as RegisterPage4Test };

// noinspection JSUnusedGlobalSymbols
export default RegisterPageV2Hoc;
